import React, {useEffect} from 'react'
import PropTypes from 'prop-types'

import {FormattedMessage, useIntl} from 'react-intl'
import {DISCOUNT_AMOUNT_LABEL, SALES_TAX_LABEL} from '@app/constants'
import {Box, Flex, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import ItemVariantProvider from '../item-variant'
import CartItemVariantImage from '../../components/item-variant/item-image'
import CartItemVariantName from '../../components/item-variant/item-name'
import CartItemVariantAttributes from '../../components/item-variant/item-attributes'
import CartItemVariantPrice from '../../components/item-variant/item-price'

import {useProducts} from '@salesforce/commerce-sdk-react'
const onClient = typeof window !== 'undefined'

// Custom Hooks
import {useGTM} from '@app/hooks/use-gtm'

import {getConvertedCurrencyCode, getConvertedPrice, formatPrice} from '../../utils/price-utils'

/**
 * OrderProducts is a React component that renders a list of product variants
 * associated with an order. It fetches product details based on the provided
 * product items and displays them in a structured format.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.productItems - An array of product items, each containing
 *                                     a `productId` and other relevant details.
 * @param {string} props.currency - The currency code to be used for displaying
 *                                  product prices.
 *
 * @returns {JSX.Element} A React fragment containing a list of product variants
 *                        with their respective images, names, prices, and attributes.
 *
 * @example
 * <OrderProducts
 *   productItems={[
 *     { productId: '123', quantity: 2 },
 *     { productId: '456', quantity: 1 }
 *   ]}
 *   currency="USD"
 * />
 *
 * @remarks
 * This component utilizes the `useProducts` hook to fetch product data based on
 * the product IDs extracted from the `productItems` prop. It then maps over the
 * fetched data to create a list of product variants, which are rendered using
 * various sub-components such as `CartItemVariantImage`, `CartItemVariantName`,
 * `CartItemVariantPrice`, and `CartItemVariantAttributes`.
 *
 * The component handles loading states and ensures that product details are
 * displayed only when the data is fully loaded.
 */
const OrderProducts = ({
    productItems,
    currency,
    conversionsRate,
    shipToCountryCode,
    includeTax = false,
    includeDiscountAmount = false,
    isExchangeInvoicePrice = false,
    isRenderPricePerUnit = true
}) => {
    const intl = useIntl()
    const orderProductIds = productItems?.map((product) => product.productId)
    const {usePageLoadAndCookies} = useGTM()
    const {data: products, isLoading} = useProducts(
        {
            parameters: {
                ids: orderProductIds
            }
        },
        {
            enabled: !!orderProductIds && onClient,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )
    const variants = productItems?.map((item) => {
        const product = products?.[item.productId]
        return {
            ...(product ? product : {}),
            isProductUnavailable: !product,
            ...item
        }
    })

    usePageLoadAndCookies('account')

    return (
        <>
            {!isLoading && variants?.length > 0 && (
                <Stack as="ul" gap={16}>
                    {variants.map((variant) => {
                        const discountTotalAmount = variant.price - variant.priceAfterOrderDiscount

                        return (
                            <Box
                                key={variant.id}
                                layerStyle="accountCard"
                                as="li"
                                listStyleType="none"
                            >
                                <ItemVariantProvider variant={variant} currency={currency}>
                                    <Flex width="full" alignItems="flex-start" gap={16}>
                                        <CartItemVariantImage
                                            width={'85px'}
                                            imageProps={{width: '85px', height: '106px'}}
                                        />
                                        <Stack spacing={8} flex={1}>
                                            <CartItemVariantName />
                                            <CartItemVariantPrice
                                                currency={currency}
                                                isExchangeInvoicePrice={isExchangeInvoicePrice}
                                                isRenderPricePerUnit={isRenderPricePerUnit}
                                                conversionsRate={conversionsRate}
                                                shipToCountryCode={shipToCountryCode}
                                            />
                                            <CartItemVariantAttributes
                                                includeQuantity
                                                currency={currency}
                                                pt={8}
                                                pb={8}
                                            />

                                            {includeDiscountAmount && discountTotalAmount > 0 && (
                                                <Flex>
                                                    <Text
                                                        as="span"
                                                        color="black"
                                                        variant="bodyBase2"
                                                    >
                                                        <FormattedMessage
                                                            {...DISCOUNT_AMOUNT_LABEL}
                                                        />
                                                    </Text>
                                                    &nbsp;
                                                    <Text as="span" variant="bodyBase2">
                                                        {formatPrice(
                                                            getConvertedPrice(
                                                                discountTotalAmount,
                                                                conversionsRate,
                                                                false
                                                            ),
                                                            getConvertedCurrencyCode(
                                                                conversionsRate,
                                                                currency
                                                            ),
                                                            conversionsRate,
                                                            shipToCountryCode,
                                                            intl
                                                        )}
                                                    </Text>
                                                </Flex>
                                            )}

                                            {includeTax && (
                                                <Flex>
                                                    <Text
                                                        as="span"
                                                        color="black"
                                                        variant="bodyBase2"
                                                    >
                                                        <FormattedMessage {...SALES_TAX_LABEL} />
                                                    </Text>
                                                    &nbsp;
                                                    <Text as="span" variant="bodyBase2">
                                                        {formatPrice(
                                                            getConvertedPrice(
                                                                variant.adjustedTax,
                                                                conversionsRate,
                                                                false
                                                            ),
                                                            getConvertedCurrencyCode(
                                                                conversionsRate,
                                                                currency
                                                            ),
                                                            conversionsRate,
                                                            shipToCountryCode,
                                                            intl
                                                        )}
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Stack>
                                    </Flex>
                                </ItemVariantProvider>
                            </Box>
                        )
                    })}
                </Stack>
            )}
        </>
    )
}

OrderProducts.propTypes = {
    productItems: PropTypes.array.isRequired,
    currency: PropTypes.string,
    conversionsRate: PropTypes.object,
    shipToCountryCode: PropTypes.string,
    includeTax: PropTypes.bool,
    includeDiscountAmount: PropTypes.bool,
    isExchangeInvoicePrice: PropTypes.bool,
    isRenderPricePerUnit: PropTypes.bool
}

export default OrderProducts
